import { useMemo, useState } from 'react'
import { Alert, App, Button, Card, Descriptions, Select, Space, Tag, Typography } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { fetchOrgNodeTypes, fetchOrgTree, typeOf } from '@/features/org/orgApi'
import { fetchCashSettings, saveCashSettings } from './cashdeskApi'

export function HeadOfficeCard() {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const { can } = usePermissions()
  const queryClient = useQueryClient()
  const canEdit = can('settings.update') && can('org.node.read')
  const [editing, setEditing] = useState(false)
  const [choice, setChoice] = useState<string | null>(null)

  const settings = useQuery({
    queryKey: queryKeys.cashdesk.settings,
    queryFn: ({ signal }) => fetchCashSettings(signal),
  })

  const tree = useQuery({
    queryKey: queryKeys.org.tree,
    queryFn: ({ signal }) => fetchOrgTree(signal),
    enabled: editing,
  })
  const types = useQuery({
    queryKey: queryKeys.org.types,
    queryFn: fetchOrgNodeTypes,
    enabled: editing,
  })

  const options = useMemo(
    () =>
      (tree.data?.nodes ?? [])
        .filter((node) => node.state !== 'CLOSED' && typeOf(types.data ?? [], node.kind)?.costCenter === true)
        .map((node) => ({ value: node.id, label: `${node.displayName} (${node.code})` })),
    [tree.data, types.data],
  )

  const save = useMutation({
    mutationFn: (nodeId: string) => saveCashSettings(nodeId),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.cashdesk.settings, next)
      void message.success(t('common.toastSaved'))
      setEditing(false)
    },
  })

  const office = settings.data?.headOffice ?? null

  return (
    <Card
      title={t('cashdesk.headOffice.title')}
      extra={
        canEdit && !editing ? (
          <Button
            size="small"
            onClick={() => {
              setChoice(office?.id ?? null)
              setEditing(true)
            }}
          >
            {t('common.edit')}
          </Button>
        ) : null
      }
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Typography.Text type="secondary">{t('cashdesk.headOffice.hint')}</Typography.Text>
        {settings.isError ? <Alert type="error" showIcon message={translateError(settings.error, t)} /> : null}
        {settings.isSuccess && office === null ? (
          <Alert
            type="warning"
            showIcon
            message={t('cashdesk.headOffice.unset', { page: t('nav.orgStructure') })}
          />
        ) : null}
        {settings.isSuccess && office ? (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label={t('cashdesk.headOffice.node')}>
              <Space size={4} wrap>
                <span>{office.name}</span>
                {settings.data.configured ? null : <Tag>{t('cashdesk.headOffice.defaulted')}</Tag>}
              </Space>
            </Descriptions.Item>
          </Descriptions>
        ) : null}
        {office && (!office.costCenter || office.state !== 'ACTIVE') ? (
          <Alert type="error" showIcon message={t('cashdesk.headOffice.invalid')} />
        ) : null}

        {editing ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            {tree.isError || types.isError ? (
              <Alert type="error" showIcon message={translateError(tree.error ?? types.error, t)} />
            ) : null}
            <Select<string>
              showSearch
              optionFilterProp="label"
              value={choice ?? undefined}
              onChange={setChoice}
              loading={tree.isPending || types.isPending}
              placeholder={t('cashdesk.headOffice.placeholder')}
              options={options}
              style={{ width: '100%' }}
            />
            {save.isError ? <Alert type="error" showIcon message={translateError(save.error, t)} /> : null}
            <Space>
              <Button
                type="primary"
                disabled={choice === null}
                loading={save.isPending}
                onClick={() => choice && save.mutate(choice)}
              >
                {t('common.save')}
              </Button>
              <Button
                onClick={() => {
                  save.reset()
                  setEditing(false)
                }}
              >
                {t('common.cancel')}
              </Button>
            </Space>
          </Space>
        ) : null}
      </Space>
    </Card>
  )
}
